import { Component, forwardRef, HostBinding, Input, OnInit, } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, } from '@angular/forms';

@Component({
  selector: 'app-formatted-number-input',
  standalone: true,
  imports: [],
  templateUrl: './formatted-number-input.component.html',
  styleUrl: './formatted-number-input.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormattedNumberInputComponent),
      multi: true,
    },
  ],
})

export class FormattedNumberInputComponent implements ControlValueAccessor {
  // ── Inputs ────────────────────────────────────────────────────────────────
  /** Placeholder text shown when the field is empty */
  @Input() placeholder = 'Type here';

  /** Extra CSS classes forwarded to the <input> element */
  @Input() inputClass = 'form-control rounded-pill shadow-none input-text';

  /**
   * Bind directly to the form-control's invalid + touched state so the
   * component can apply Bootstrap's .is-invalid class:
   *
   *   [isInvalid]="contributorForm.get('...')?.invalid &&
   *                contributorForm.get('...')?.touched"
   */
  @Input() isInvalid: boolean | undefined = false;

  /**
   * Mirrors the is-invalid class onto the host <app-formatted-number-input>
   * element so Bootstrap's sibling selector `.is-invalid ~ .invalid-feedback`
   * can find the error div rendered in the parent template.
   */
  @HostBinding('class.is-invalid') get hostIsInvalid() {
    return this.isInvalid === true;
  }

  /** Number of decimal places allowed (default: 2) */
  @Input() decimalPlaces = 2;

  /** Minimum allowed value (mirrors the native min attribute) */
  @Input() min: number | null = null;

  /** Maximum allowed value (mirrors the native max attribute) */
  @Input() max: number | null = null;

  // ── Internal state ────────────────────────────────────────────────────────
  displayValue = '';
  isDisabled = false;

  // ── CVA callbacks ─────────────────────────────────────────────────────────
  private _onChange: (value: number | null) => void = () => { };
  private _onTouched: () => void = () => { };

  // ── ControlValueAccessor ─────────────────────────────────────────────────

  /**
   * Called by Angular when the model value changes from outside
   * (e.g. patchValue / setValue / data load).
   * We format whatever arrives so the user always sees commas.
   */
  writeValue(value: number | string | null | undefined): void {
    if (value === null || value === undefined || value === '') {
      this.displayValue = '';
      return;
    }
    const num = typeof value === 'string' ? parseFloat(value) : value;
    this.displayValue = isNaN(num)
      ? ''
      : this.formatNumber(num.toFixed(this.decimalPlaces));
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  // ── Event handlers ────────────────────────────────────────────────────────

  /**
   * Fires on every keystroke.
   * 1. Records how many "real" characters (digits / dot) sit before the caret.
   * 2. Strips commas & invalid chars from the raw value.
   * 3. Re-formats with commas.
   * 4. Restores the caret to the logically equivalent position.
   * 5. Emits the plain numeric value to the reactive form.
   */
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;
    const cursorPos = input.selectionStart ?? rawValue.length;

    const realCharsBeforeCursor = rawValue.substring(0, cursorPos).replace(/,/g, '').length;

    let cleaned = rawValue.replace(/,/g, '').replace(/[^0-9.]/g, '');
    const firstDot = cleaned.indexOf('.');
    if (firstDot !== -1) {
      cleaned = cleaned.substring(0, firstDot + 1) +
        cleaned.substring(firstDot + 1).replace(/\./g, '');
    }
    if (firstDot !== -1) {
      const intPart = cleaned.substring(0, firstDot + 1);
      const decPart = cleaned.substring(firstDot + 1, firstDot + 1 + this.decimalPlaces);
      cleaned = intPart + decPart;
    }

    const formatted = this.formatNumber(cleaned);
    const numericValue = cleaned === '' || cleaned === '.' ? null : parseFloat(cleaned);

    // ── Clamp max ────────────────────────────────────────────────────────────
    if (numericValue !== null && this.max !== null && numericValue > this.max) {
      const clamped = this.formatNumber(this.max.toFixed(this.decimalPlaces));
      this.displayValue = clamped;
      input.value = clamped;            // ← patch DOM immediately
      this._onChange(this.max);
      return;
    }

    // ── Clamp min ────────────────────────────────────────────────────────────
    if (numericValue !== null && this.min !== null && numericValue < this.min) {
      const clamped = this.formatNumber(this.min.toFixed(this.decimalPlaces));
      this.displayValue = clamped;
      input.value = clamped;            // ← patch DOM immediately
      this._onChange(this.min);
      return;
    }

    this.displayValue = formatted;
    input.value = formatted;            // ← keep DOM in sync always

    const newCursorPos = this.findCursorPos(formatted, realCharsBeforeCursor);
    setTimeout(() => input.setSelectionRange(newCursorPos, newCursorPos));

    this._onChange(numericValue);
  }

  /**
   * On blur: mark as touched, and tidy up the display value
   * (e.g. "1234." → "1,234.00", "." → "").
   */
  onBlur(): void {
    this._onTouched();

    if (!this.displayValue) return;

    const cleaned = this.displayValue.replace(/,/g, '');
    const num = parseFloat(cleaned);

    if (!isNaN(num)) {
      this.displayValue = this.formatNumber(num.toFixed(this.decimalPlaces));
    } else {
      // Edge case: user typed just a "."
      this.displayValue = '';
      this._onChange(null);
    }
  }

  /**
   * Intercept paste so users can paste values like "1,234,567.89"
   * or "1234567.89" and both format correctly.
   */
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text/plain') ?? '';
    const cleaned = pasted.replace(/,/g, '').replace(/[^0-9.]/g, '');
    let num = parseFloat(cleaned);

    if (isNaN(num)) return;

    // Clamp to max
    if (this.max !== null && num > this.max) num = this.max;

    // Clamp to min
    if (this.min !== null && num < this.min) num = this.min;

    this.displayValue = this.formatNumber(num.toFixed(this.decimalPlaces));
    this._onChange(num);
  }

  /**
   * Block keys that should never reach the input:
   * anything that isn't a digit, decimal point, or navigation key.
   */
  onKeyDown(event: KeyboardEvent): void {
    if (
      ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']
        .includes(event.key)
    ) return;

    if (event.ctrlKey || event.metaKey) return;

    if (/^\d$/.test(event.key)) {
      const input = event.target as HTMLInputElement;
      const current = this.displayValue;          // ← use component state, not input.value
      const selStart = input.selectionStart ?? current.length;
      const selEnd = input.selectionEnd ?? current.length;

      // Decimal-place cap
      const dotIndex = current.indexOf('.');
      if (dotIndex !== -1) {
        const decimalPart = current.substring(dotIndex + 1);
        const selectionLength = selEnd - selStart;
        if (selStart > dotIndex && selectionLength === 0 && decimalPart.length >= this.decimalPlaces) {
          event.preventDefault();
          return;
        }
      }

      // Simulate resulting value and enforce min/max
      const simulated = current.substring(0, selStart) + event.key + current.substring(selEnd);
      const cleaned = simulated.replace(/,/g, '').replace(/[^0-9.]/g, '');
      const simNum = parseFloat(cleaned);

      if (!isNaN(simNum)) {
        if (this.max !== null && simNum > this.max) { event.preventDefault(); return; }
        if (this.min !== null && simNum < this.min) { event.preventDefault(); return; }
      }
      return;
    }

    if (event.key === '.') {
      const input = event.target as HTMLInputElement;
      if (input.value.includes('.')) event.preventDefault();
      return;
    }

    event.preventDefault();
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  /**
   * Adds thousand-separator commas to a numeric string.
   * Preserves a trailing dot so users can keep typing decimals.
   *
   * Examples:
   *   "1234567"     → "1,234,567"
   *   "1234567.89"  → "1,234,567.89"
   *   "1234."       → "1,234."
   */
  private formatNumber(value: string): string {
    if (!value || value === '.') return value;

    const [intPart, ...rest] = value.split('.');
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return rest.length ? `${formattedInt}.${rest.join('.')}` : formattedInt;
  }

  /**
   * After re-formatting the string, find the index where the caret should sit
   * by counting `targetCount` non-comma characters from the left.
   */
  private findCursorPos(formatted: string, targetCount: number): number {
    if (targetCount <= 0) return 0;

    let realCount = 0;
    for (let i = 0; i < formatted.length; i++) {
      if (formatted[i] !== ',') realCount++;
      if (realCount === targetCount) return i + 1;
    }

    return formatted.length;
  }
}